/**
 * 权限模块
 * 负责拉取后端返回的菜单与按钮权限，并转换为动态路由配置。
 */
import { queryPermissionsByUser } from "@/servers/api";
import { buildDynamicRoutes, extractVisibleMenus } from "@/utils/permission";

// 约定外部不会返回的额外受保护页面，需本地手动合并。
const extraProtectedRoutes = [
  {
    path: "/personalCenterPassword",
    name: "personalCenterPassword",
    meta: {
      title: "修改密码",
      isCheckToken: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "personalCenterPassword" */ "@/views/personalCenter/personalCenterPassword.vue"
      ),
  },
  {
    path: "/detail",
    name: "detail",
    meta: {
      title: "细览页面",
      isCheckToken: true,
      alive: true,
      isFooter: true,
      isScrollTop: true,
    },
    component: () =>
      import(/* webpackChunkName: "detail" */ "@/views/Detail/Detail.vue"),
  },
  {
    path: "/imageDetail",
    name: "imageDetail",
    meta: {
      title: "图片细览页",
      isCheckToken: true,
      alive: true,
      isFooter: true,
      isScrollTop: true,
    },
    component: () =>
      import(/* webpackChunkName: "imageDetail" */ "@/views/Detail/imageDetail.vue"),
  },
  {
    path: "/source",
    name: "source",
    meta: {
      title: "报史资料",
      isCheckToken: true,
      alive: false,
      isFooter: false,
      isScrollTop: false,
    },
    component: () =>
      import(/* webpackChunkName: "homeSource" */ "@/views/Home/HomeSource.vue"),
  },
  // 年鉴/期刊模块：详情页与概览页通常不在后端菜单中，但会通过新开页直达
  {
    path: "/yearbook/yearbookDetail",
    name: "yearbookDetail",
    meta: {
      title: "期刊详情",
      isCheckToken: true,
      alive: true,
      isFooter: true,
      isScrollTop: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "yearbookDetail" */ "@/views/yearbook/yearbookDetail.vue"
      ),
  },
  {
    path: "/yearbook/yearbookOverview",
    name: "yearbookOverview",
    meta: {
      title: "期刊浏览",
      isCheckToken: true,
      alive: true,
      isFooter: true,
      isScrollTop: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "yearbookOverview" */ "@/views/yearbook/yearbookOverview.vue"
      ),
  },
  {
    path: "/subject/detail",
    name: "SubjectDetail",
    meta: {
      title: "专题详情",
      isCheckToken: true,
      alive: true,
      isFooter: true,
      isScrollTop: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "SubjectDetail" */ "@/views/subject/SubjectDetail.vue"
      ),
  },
];

const USER_AUTH_KEY = "USER_AUTH";
const SYS_BUTTON_AUTH_KEY = "SYS_BUTTON_AUTH";
const DATA_AUTH_KEY = "DATA_AUTH";

// Vuex state：统一维护权限相关的临时态。
const state = {
  menuTree: [],
  addRoutes: [],
  loaded: false,
  auth: [],
  allAuth: [],
  dataAuth: [],
};

// 同步修改入口：确保各字段赋值逻辑集中管理。
const mutations = {
  SET_MENU_TREE(state, menu) {
    state.menuTree = Array.isArray(menu) ? menu : [];
  },
  SET_ADD_ROUTES(state, routes) {
    state.addRoutes = Array.isArray(routes) ? routes : [];
  },
  SET_LOADED(state, flag) {
    state.loaded = !!flag;
  },
  SET_AUTH(state, payload) {
    state.auth = payload.auth || [];
    state.allAuth = payload.allAuth || [];
    state.dataAuth = payload.dataAuth || [];
  },
};

/**
 * 合并附加路由，避免重复注入。
 * @param {Array} routes 动态生成的路由集合
 * @param {Array} extras 需要强制追加的受保护路由
 * @returns {Array} 合并后的新路由数组
 */
function mergeExtraRoutes(routes, extras) {
  const exists = new Set((routes || []).map((item) => item.path));
  const additions = (extras || []).filter(
    (item) => item && item.path && !exists.has(item.path)
  );
  return [...(routes || []), ...additions];
}

const actions = {
  /**
   * 拉取权限并生成路由；只执行一次，避免重复请求。
   */
  async GenerateRoutes({ state, commit }) {
    if (state.loaded) {
      return state.addRoutes;
    }
    const response = await queryPermissionsByUser();
    const menuData = response?.result?.menu || [];
    const authData = response?.result?.auth || [];
    const allAuthData = response?.result?.allAuth || [];
    const dataAuth = response?.result?.dataAuth || [];

    sessionStorage.setItem(USER_AUTH_KEY, JSON.stringify(authData));
    sessionStorage.setItem(SYS_BUTTON_AUTH_KEY, JSON.stringify(allAuthData));
    sessionStorage.setItem(DATA_AUTH_KEY, JSON.stringify(dataAuth));

    commit("SET_MENU_TREE", menuData);
    commit("SET_AUTH", { auth: authData, allAuth: allAuthData, dataAuth });

    const routes = buildDynamicRoutes(menuData);
    const mergedRoutes = mergeExtraRoutes(routes, extraProtectedRoutes);
    commit("SET_ADD_ROUTES", mergedRoutes);
    commit("SET_LOADED", true);

    return mergedRoutes;
  },
  /**
   * 清空权限相关缓存，用于退出或强制刷新。
   */
  ResetPermission({ commit }) {
    commit("SET_MENU_TREE", []);
    commit("SET_ADD_ROUTES", []);
    commit("SET_LOADED", false);
    commit("SET_AUTH", { auth: [], allAuth: [], dataAuth: [] });
    sessionStorage.removeItem(USER_AUTH_KEY);
    sessionStorage.removeItem(SYS_BUTTON_AUTH_KEY);
    sessionStorage.removeItem(DATA_AUTH_KEY);
  },
};

const getters = {
  // 菜单树：用于渲染左侧导航。
  menuTree: (state) => state.menuTree,
  // 顶部菜单：根据配置筛出可见项。
  topMenus: (state) => extractVisibleMenus(state.menuTree),
  // 是否已经加载权限。
  isLoaded: (state) => state.loaded,
  // 动态追加的路由集合。
  addRoutes: (state) => state.addRoutes,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
